import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/i18n/LanguageContext";

// Desser "Six Signatures" — an editorial 6-card mosaic celebrating each
// flavour profile in the Desser range. Cards are colour-swatch tiles (no
// bottle photo required — the swatch IS the identity) featuring a poetic name,
// the flavour composition, one suggested usage moment, and the Desser dandy
// emblem watermark. Sits below DesserHero on the Desser brand page.
// -----------------------------------------------------------------------------
const COCOA = "#1A0808";
const GOLD = "#E8B14B";
const CREAM = "#F7E2BB";

// Each signature carries its own accent colour tuned to its dominant flavour.
// tint = light card surface; ink = text colour on that surface.
// Every Desser flavour is a triple-duty ingredient — usable for coffee, cocktails
// AND pastry — so all three usage badges appear on every card.
const ALL_MOMENTS = ["coffee", "cocktail", "pastry"];
const SIGNATURES = [
  { key: "deMer", accent: "#C79852", tint: "#F3E6CB", ink: "#3E260E" },
  { key: "madagascar", accent: "#D67991", tint: "#F7DDE4", ink: "#3E1420" },
  { key: "peruvian", accent: "#5E2E1C", tint: "#E2CBB8", ink: "#2A140A" },
  { key: "ivory", accent: "#B8A272", tint: "#EEE4C9", ink: "#3D2E14" },
  { key: "belgravia", accent: "#7F9166", tint: "#DBE3C4", ink: "#26301A" },
  { key: "silken", accent: "#8F6141", tint: "#E8CFBA", ink: "#341C0F" },
];

const FALLBACK = {
  deMer: {
    name: "De Mer Whisper",
    flavour: "Salted Caramel",
    body: "A whisper of ocean salt over slow-cooked butterscotch. Poured over a whisky sour or a rich flat white.",
  },
  madagascar: {
    name: "Madagascar Charm",
    flavour: "Vanilla · Framboise",
    body: "Bourbon vanilla married with raspberry brightness. A house pour for spritzes and French 75s.",
  },
  peruvian: {
    name: "Peruvian Noir",
    flavour: "Dark Chocolate Truffle",
    body: "70% cacao richness with truffle depth. Turns a mocha into a moment, a fondant into a memory.",
  },
  ivory: {
    name: "Ivory Pearl",
    flavour: "White Chocolate · Macadamia",
    body: "Silken white chocolate with toasted macadamia warmth. Ribbons beautifully into cheesecake and gelato.",
  },
  belgravia: {
    name: "Belgravia Celestial",
    flavour: "White Choc · Pistachio · Banana",
    body: "The house's most theatrical composition. Pistachio-and-banana over creamy white chocolate — built for layered desserts.",
  },
  silken: {
    name: "Silken Mist",
    flavour: "Dulce de Leche · Almond",
    body: "Caramelised milk with toasted almond softness. A signature for lattes, tiramisus and slow afternoons.",
  },
};

const MOMENT_LABELS = {
  coffee: { en: "Coffee", tr: "Kahve", fr: "Café", ru: "Кофе", zh: "咖啡", ar: "قهوة" },
  cocktail: { en: "Cocktails", tr: "Kokteyller", fr: "Cocktails", ru: "Коктейли", zh: "鸡尾酒", ar: "كوكتيلات" },
  pastry: { en: "Pastry", tr: "Pastane", fr: "Pâtisserie", ru: "Десерты", zh: "甜点", ar: "حلويات" },
};

function SignatureCard({ signature, index }) {
  const { t, lang } = useT();
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("brandContent.");
  const fb = FALLBACK[signature.key];
  const nameRaw = t(`brandContent.desser.signatures.${signature.key}.name`);
  const flavourRaw = t(`brandContent.desser.signatures.${signature.key}.flavour`);
  const bodyRaw = t(`brandContent.desser.signatures.${signature.key}.body`);
  const name = isTranslated(nameRaw) ? nameRaw : fb.name;
  const flavour = isTranslated(flavourRaw) ? flavourRaw : fb.flavour;
  const body = isTranslated(bodyRaw) ? bodyRaw : fb.body;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08 }}
      className="relative overflow-hidden rounded-2xl p-8 lg:p-10 min-h-[320px] flex flex-col justify-between"
      style={{ background: signature.tint, color: signature.ink }}
      data-testid={`desser-sig-${signature.key}`}
    >
      {/* Watermark emblem — top-hat wordmark, very low opacity */}
      <img
        aria-hidden
        src="/logos/desser.webp"
        alt=""
        className="pointer-events-none select-none absolute right-[-15%] bottom-[-20%] w-[75%] max-w-none opacity-[0.08]"
      />

      <div className="relative">
        {/* Three moment badges — every Desser flavour works for all three */}
        <div className="flex flex-wrap gap-1.5">
          {ALL_MOMENTS.map((m) => (
            <span
              key={m}
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[0.6rem] font-bold uppercase tracking-[0.28em]"
              style={{ background: signature.accent, color: signature.tint }}
            >
              {MOMENT_LABELS[m]?.[lang] || MOMENT_LABELS[m].en}
            </span>
          ))}
        </div>

        <h3
          className="mt-6 font-editorial italic leading-[1.05] text-3xl md:text-4xl"
          style={{ color: signature.ink }}
        >
          {name}
        </h3>

        <p
          className="mt-2 text-xs font-bold uppercase"
          style={{ color: signature.accent, letterSpacing: "0.28em" }}
        >
          {flavour}
        </p>
      </div>

      <p
        className="relative mt-6 text-sm md:text-[15px] leading-[1.65]"
        style={{ color: `${signature.ink}dd` }}
      >
        {body}
      </p>
    </motion.article>
  );
}

export default function DesserSignatures() {
  const { t } = useT();
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("brandContent.");
  const overlineRaw = t("brandContent.desser.signatures.overline");
  const overline = isTranslated(overlineRaw) ? overlineRaw : "The Six Signatures";
  const titleRaw = t("brandContent.desser.signatures.title");
  const title = isTranslated(titleRaw)
    ? titleRaw
    : "Six flavours. Six moments to compose.";

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: COCOA, color: CREAM }}
      data-testid="desser-signatures"
    >
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
        {/* Section header */}
        <div className="max-w-2xl mb-14">
          <p
            className="text-[0.7rem] md:text-xs font-bold uppercase mb-4"
            style={{ color: GOLD, letterSpacing: "0.36em" }}
          >
            {overline}
          </p>
          <h2
            className="font-editorial italic leading-[1.05] text-[clamp(2.25rem,4.4vw,3.75rem)]"
            style={{ color: CREAM }}
          >
            {title}
          </h2>
          <div
            className="mt-6 h-px w-24"
            style={{ background: `linear-gradient(90deg, ${GOLD}, transparent)` }}
          />
        </div>

        {/* 2×3 mosaic on desktop, single column on mobile */}
        <div className="grid gap-5 md:gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SIGNATURES.map((s, i) => (
            <SignatureCard key={s.key} signature={s} index={i} />
          ))}
        </div>
      </div>

      {/* Fine bottom divider */}
      <div
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 5%, ${GOLD} 20%, ${GOLD} 80%, transparent 95%)`,
        }}
      />
    </section>
  );
}
